import { ANALYTICS_EVENTS, KYB_ACCOUNT_TYPE } from '@config';
import { useAnalytics } from '@hooks';
import { useCompliance } from '@hooks/useCompliance';
import { mapDirector, mapShareholder, mapUBO } from '@utils/compliance-mappers';
import { useMemo } from 'react';
import { toast } from 'react-toastify';

const useOwnershipSubmit = ({
  directors,
  shareholders,
  ubos,
  particularsFile,
  certifiedListFile,
  particularsAttachmentId,
  certifiedListAttachmentId,
  uboAttachmentIds,
  directorEntryIds,
  shareholderEntryIds,
  uboEntryIds,
  setParticularsFile,
  setParticularsAttachmentId,
  setCertifiedListFile,
  setCertifiedListAttachmentId,
  setDirectorEntryIds,
  setShareholderEntryIds,
  setUboEntryIds,
  setUboAttachmentIds,
  onSaveDraft
}) => {
  const { track } = useAnalytics();
  const { uploadFile, saveSection, deleteEntry } = useCompliance();

  const saveDraft = (overrides) => {
    onSaveDraft?.({
      directors,
      shareholders,
      ubos,
      particulars_file: particularsFile,
      certified_list_file: certifiedListFile,
      ...overrides
    });
  };

  const SECTION_FILE_CONFIG = {
    particulars: {
      setFile: setParticularsFile,
      setAttachmentId: setParticularsAttachmentId,
      draftKey: 'particulars_file',
      successMessage: 'Particulars of Directors uploaded successfully',
      event: ANALYTICS_EVENTS.KYB_DIRECTORS_FILE_UPLOADED
    },
    certified_list: {
      setFile: setCertifiedListFile,
      setAttachmentId: setCertifiedListAttachmentId,
      draftKey: 'certified_list_file',
      successMessage: 'Certified List of Shareholders uploaded successfully',
      event: ANALYTICS_EVENTS.KYB_SHAREHOLDERS_FILE_UPLOADED
    }
  };

  const handleSectionFileChange = async (field, file) => {
    if (!file) return;

    const config = SECTION_FILE_CONFIG[field];
    config.setFile(file);

    try {
      const result = await uploadFile(file);
      config.setAttachmentId(result.attachment_id);
      toast.success(config.successMessage);
      track(config.event, { kyb_account_type: KYB_ACCOUNT_TYPE.CORPORATE_NON_FI });
    } catch (error) {
      config.setFile(null);
      config.setAttachmentId(null);
      toast.error('Upload failed. Please try again.');
    }
  };

  const handleDeleteFile = (fileDeleteTarget) => {
    track(ANALYTICS_EVENTS.KYB_DOC_DELETED, {
      kyb_account_type: KYB_ACCOUNT_TYPE.CORPORATE_NON_FI,
      doc_type: fileDeleteTarget
    });
    const config = SECTION_FILE_CONFIG[fileDeleteTarget];
    if (config) {
      config.setFile(null);
      config.setAttachmentId(null);
      saveDraft({ [config.draftKey]: null });
    }
  };

  const handleDeletePerson = async (deleteTarget, setters) => {
    const { type, id } = deleteTarget;
    const eventMap = {
      director: ANALYTICS_EVENTS.KYB_DIRECTOR_REMOVED,
      shareholder: ANALYTICS_EVENTS.KYB_SHAREHOLDER_REMOVED,
      ubo: ANALYTICS_EVENTS.KYB_UBO_REMOVED
    };
    track(eventMap[type], { kyb_account_type: KYB_ACCOUNT_TYPE.CORPORATE_NON_FI });

    try {
      const sectionMap = {
        director: 'OWNERSHIP_DIRECTORS',
        shareholder: 'OWNERSHIP_SHAREHOLDERS',
        ubo: 'OWNERSHIP_UBOS'
      };
      const entryIdMap = {
        director: directorEntryIds,
        shareholder: shareholderEntryIds,
        ubo: uboEntryIds
      };

      const entryId = entryIdMap[type][id];
      if (entryId) {
        await deleteEntry(sectionMap[type], entryId);
      }

      if (type === 'director') {
        setters.setDirectors((prev) => prev.filter((d) => d.id !== id));
        setDirectorEntryIds((prev) => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
      } else if (type === 'shareholder') {
        setters.setShareholders((prev) => prev.filter((s) => s.id !== id));
        setShareholderEntryIds((prev) => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
      } else if (type === 'ubo') {
        setters.setUbos((prev) => prev.filter((u) => u.id !== id));
        setUboEntryIds((prev) => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
        setUboAttachmentIds((prev) => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to delete person:', error);
      toast.error('Failed to delete person');
    }
  };

  const isFormValid = useMemo(() => {
    if (!directors.length || !shareholders.length || !ubos.length) return false;
    if (!particularsAttachmentId || !certifiedListAttachmentId) return false;
    const directorsValid = directors.every(
      (d) => d.name && d.is_pep && /^\d{11}$/.test(d.bvn || '')
    );
    const shareholdersValid = shareholders.every((s) => s.name && s.is_pep);
    const ubosValid = ubos.every(
      (u) =>
        u.name &&
        u.is_pep &&
        u.id_type &&
        u.id_number &&
        (u.means_of_id_file || u.means_of_identification_attachment_id || uboAttachmentIds[u.id])
    );
    return directorsValid && shareholdersValid && ubosValid;
  }, [
    directors,
    shareholders,
    ubos,
    uboAttachmentIds,
    particularsAttachmentId,
    certifiedListAttachmentId
  ]);

  const validate = () => {
    const errs = {};

    if (!directors.length) errs.directors = 'At least one director is required';
    directors.forEach((d, i) => {
      if (!d.name) errs[`director_${i}_name`] = 'Director name is required';
      if (!d.is_pep) errs[`director_${i}_is_pep`] = 'Please select an option';
      if (!/^\d{11}$/.test(d.bvn || ''))
        errs[`director_${i}_bvn`] = 'BVN must be exactly 11 digits';
    });
    if (!particularsAttachmentId && !particularsFile) {
      errs.particulars_file = 'Particulars of Directors document is required';
    }

    if (!shareholders.length) errs.shareholders = 'At least one shareholder is required';
    shareholders.forEach((s, i) => {
      if (!s.name) errs[`shareholder_${i}_name`] = 'Shareholder name is required';
      if (!s.is_pep) errs[`shareholder_${i}_is_pep`] = 'Please select an option';
    });
    if (!certifiedListAttachmentId && !certifiedListFile)
      errs.certified_list_file = 'Certified List of Shareholders document is required';

    if (!ubos.length) errs.ubos = 'At least one UBO is required';
    ubos.forEach((u, i) => {
      if (!u.name) errs[`ubo_${i}_name`] = 'UBO name is required';
      if (!u.is_pep) errs[`ubo_${i}_is_pep`] = 'Please select an option';
      if (!u.id_type) errs[`ubo_${i}_id_type`] = 'ID type is required';
      if (!u.id_number) errs[`ubo_${i}_id_number`] = 'ID number is required';
      if (
        !u.means_of_id_file &&
        !u.means_of_identification_attachment_id &&
        !uboAttachmentIds[u.id]
      ) {
        errs[`ubo_${i}_means_of_id_file`] = 'Means of ID document is required';
      }
    });

    return errs;
  };

  const handleSubmit = async ({ onComplete, setIsSubmitting }) => {
    const errs = validate();
    if (Object.keys(errs).length > 0) return errs;

    try {
      setIsSubmitting(true);

      track(ANALYTICS_EVENTS.KYB_OWNERS_SAVED, {
        kyb_account_type: KYB_ACCOUNT_TYPE.CORPORATE_NON_FI
      });

      // Save directors
      const currentDirEntryIds = { ...directorEntryIds };
      for (let i = 0; i < directors.length; i++) {
        const director = directors[i];
        const apiData = mapDirector(director);
        const result = await saveSection('OWNERSHIP_DIRECTORS', apiData, {
          entryId: currentDirEntryIds[director.id],
          position: i
        });
        if (result.entry_id) {
          currentDirEntryIds[director.id] = result.entry_id;
          setDirectorEntryIds((prev) => ({ ...prev, [director.id]: result.entry_id }));
        }
      }

      // Save shareholders
      const currentShEntryIds = { ...shareholderEntryIds };
      for (let i = 0; i < shareholders.length; i++) {
        const shareholder = shareholders[i];
        const apiData = mapShareholder(shareholder);
        const result = await saveSection('OWNERSHIP_SHAREHOLDERS', apiData, {
          entryId: currentShEntryIds[shareholder.id],
          position: i
        });
        if (result.entry_id) {
          currentShEntryIds[shareholder.id] = result.entry_id;
          setShareholderEntryIds((prev) => ({ ...prev, [shareholder.id]: result.entry_id }));
        }
      }

      // Save UBOs
      const currentUboEntryIds = { ...uboEntryIds };
      for (let i = 0; i < ubos.length; i++) {
        const ubo = ubos[i];
        let attachmentId = uboAttachmentIds[ubo.id] || ubo.means_of_identification_attachment_id;

        if (ubo.means_of_id_file && !attachmentId) {
          const result = await uploadFile(ubo.means_of_id_file);
          attachmentId = result.attachment_id;
          setUboAttachmentIds((prev) => ({ ...prev, [ubo.id]: attachmentId }));
        }

        if (!attachmentId) {
          throw new Error(`UBO ${ubo.name} missing ID document attachment`);
        }

        const apiData = mapUBO(ubo, attachmentId);
        const result = await saveSection('OWNERSHIP_UBOS', apiData, {
          entryId: currentUboEntryIds[ubo.id],
          position: i
        });
        if (result.entry_id) {
          currentUboEntryIds[ubo.id] = result.entry_id;
          setUboEntryIds((prev) => ({ ...prev, [ubo.id]: result.entry_id }));
        }
      }

      // Save ownership documents section
      if (particularsAttachmentId && certifiedListAttachmentId) {
        await saveSection('OWNERSHIP_DOCUMENTS', {
          particulars_of_directors_attachment_id: particularsAttachmentId,
          certified_shareholder_register_attachment_id: certifiedListAttachmentId
        });
      }

      toast.success('Ownership Structure saved successfully');
      onComplete({
        directors,
        shareholders,
        ubos,
        particulars_file: particularsFile,
        certified_list_file: certifiedListFile,
        particulars_attachment_id: particularsAttachmentId,
        certified_list_attachment_id: certifiedListAttachmentId
      });

      return null;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to save ownership structure:', error);
      return null;
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    saveDraft,
    handleSectionFileChange,
    handleDeleteFile,
    handleDeletePerson,
    isFormValid,
    validate,
    handleSubmit
  };
};

export { useOwnershipSubmit };
